// @flow
import moment from 'moment'
import buildRideLocation from './rideLocation'
import { rideTrackingAmenity } from './tripAmenity'
import { PRICE_TYPE } from '../../../constants/priceType'

// Types
import type { Step } from '../../../models/step'
import type { OndemandSearchStep } from '../../../api_types'

type PartialResponse = {
  flight_datetime?: string,
  pickup_datetime?: string,
  num_passengers: number,
}

/**
 * Build step for ondemand trip
 * @param  {Object} step     Step object of the trip
 * @param  {Object} trip     The trip object
 * @param  {Object} response The whole search response object
 * @return {Object}
 * @private
 */
function buildOndemandStep(
  step: OndemandSearchStep,
  response: PartialResponse
): Step {
  const startLocation = step.details.start_location
    ? buildRideLocation(step.details.start_location)
    : null
  const endLocation = step.details.end_location
    ? buildRideLocation(step.details.end_location)
    : null
  const accurateTime =
    ((step.details.estimates && step.details.estimates.duration_seconds) || 0) /
    60
  const departureDatetime = moment.parseZone(
    response.pickup_datetime || response.flight_datetime
  )
  const arrivalDatetime = departureDatetime
    .clone()
    .add(accurateTime || 30, 'minutes')
  const relevantPrice = step.details.cost || step.details.estimates || {}
  const cancelPenalty = relevantPrice.cancel_penalty_amount
  const currencyObj = relevantPrice.currency || {}
  const waitTime = parseInt(
    (step.details.cost && step.details.cost.waiting_minute) || '0',
    10
  )
  const penaltyValue = (cancelPenalty && parseFloat(cancelPenalty.value)) || 0
  const cancellationPolicy = []

  if (relevantPrice && cancelPenalty && penaltyValue > 0) {
    cancellationPolicy.push({
      notice: 1,
      refundPercent: 0,
      penaltyPrice: {
        total: cancelPenalty.compact,
        slash: '',
        raw: penaltyValue,
        currencyCode: currencyObj.code,
        currencySymbol: currencyObj.sign,
        type: PRICE_TYPE.FIXED_PRICE,
      },
    })
  }

  return {
    from: {
      location: startLocation,
      distance: 0,
    },
    to: {
      location: endLocation,
      distance: 0,
    },
    description: step.details.description || '',
    main: step.main,
    alternativeTimesIndex: -1,
    alternativeTimes: [],
    serviceHours: {},
    ticketTypes: [],
    passTypes: [],
    arrivalTimes: [],
    arrivalDatetime,
    departureDatetime,
    type: step.step_type,
    bookingDetails: {
      gratuityIncluded: true,
      tollsIncluded: true,
      meetAndGreetDefault: false,
      meetAndGreetAvailable: false,
      meetAndGreetPrice: '',
      meetAndGreetRawPrice: 0,
      cancellationPolicy,
      cancellableOffline: false, // TODO check
      cancellableOnline: true,
      amendable: true,
      waitTime,
      extraPaxRequired: false,
      acceptsGratuity: false,
      showFlightInfo: step.details.reservation_information.ask_flight_number,
      areTermsInternal: false,
      termsOfServiceURL: '',
      notes: '',
      isBookable: true,
      startTime: null,
      endTime: null,
      hourlyDetails: null,
      showTollsAndGratuity: false,
      startAddressIncomplete: false,
      endAddressIncomplete: false,
      specialInstructionsPlaceholder: '',
      oauthAuthorizeUrl: step.details.oauth_authorize_url,
      askPayment: step.details.reservation_information.ask_payment,
    },
    vehicle: {
      type: {
        id: step.details.vehicle.vehicle_type.key, // FE should use this id
        key: step.details.vehicle.vehicle_type.name, // And this for compatibility
        name: step.details.vehicle.vehicle_type.name,
      },
      maxPassengers: step.details.vehicle.max_passengers,
      maxBags: step.details.vehicle.max_bags,
      isMaxBagsPerPerson: step.details.vehicle.is_max_bags_per_person,
      imageUrl: step.details.vehicle.image || step.details.provider.logo || '',
      numVehicles: step.details.vehicle.num_vehicles || 1,
      totalBags: step.details.vehicle.total_bags,
      make: step.details.vehicle.make || '',
      model: step.details.vehicle.model || '',
      vehicleClass:
        step.details.vehicle.vehicle_class_detail?.vehicle_class_id || null, // eslint-disable-line camelcase
      vehicleClassName:
        step.details.vehicle.vehicle_class_detail?.display_name || '', // eslint-disable-line camelcase
    },
    provider: {
      email: '',
      phoneNumber: '',
      logoUrl: step.details.provider.logo || step.details.vehicle.image || '',
      name: step.details.display_name || step.details.provider.name,
      realName: step.details.provider.name || step.details.display_name,
      rating: parseFloat(step.details.provider.rating),
      ratingFloat: parseFloat(step.details.provider.rating_with_decimals),
      ratingCount: step.details.provider.rating_count,
      supplierScore: -1,
    },
    amenities: [rideTrackingAmenity],
    passengersCount: response.num_passengers,
    accurateTime,
  }
}

export default buildOndemandStep
